import { CheckboxInput, MiniIconInformation } from '@newfront-insurance/core-ui';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  cn,
  Input,
} from '@newfront-insurance/core-ui/v2';
import { Loader2, Plus, X } from 'lucide-react';
import { useEffect, useRef, useState, type MutableRefObject, type ReactNode } from 'react';

import type { SettingsPanelCloseGuard } from './settings-panel-context';
import { useUnsavedNovaChanges } from '../navigation/use-unsaved-nova-changes';

export interface EmailGroup {
  id: string;
  displayName: string;
  mail: string;
}

export interface SharedInbox {
  id: string;
  email: string;
  enabled: boolean;
}

export interface OutlookMailboxSettingsProps {
  groups: EmailGroup[];
  selectedGroupIds: Set<string>;
  sharedInboxes: SharedInbox[];
  isLoading: boolean;
  error: string | null;
  isDirty: boolean;
  toggleGroup: (groupId: string) => void;
  selectAllGroups: () => void;
  deselectAllGroups: () => void;
  addSharedInbox: (email: string) => boolean;
  removeSharedInbox: (id: string) => void;
  toggleSharedInbox: (id: string) => void;
  save: () => void;
  onCancel?: () => void;
  onSaveSuccess?: () => void;
  /** Ref that exposes the cancel handler so parents can trigger it imperatively */
  cancelRef?: MutableRefObject<SettingsPanelCloseGuard | null>;
  className?: string;
  children?: ReactNode;
}

export function OutlookMailboxSettings({
  groups,
  selectedGroupIds,
  sharedInboxes,
  isLoading,
  error,
  isDirty,
  toggleGroup,
  selectAllGroups,
  deselectAllGroups,
  addSharedInbox,
  removeSharedInbox,
  toggleSharedInbox,
  save,
  onCancel,
  onSaveSuccess,
  cancelRef,
  className,
  children,
}: OutlookMailboxSettingsProps) {
  const [newInboxEmail, setNewInboxEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const pendingCancelResolverRef = useRef<((canClose: boolean) => void) | null>(null);
  useUnsavedNovaChanges(isDirty || Boolean(newInboxEmail.trim()));

  const requestCancel: SettingsPanelCloseGuard = () => {
    if (!isDirty) {
      return true;
    }

    pendingCancelResolverRef.current?.(false);
    setShowUnsavedDialog(true);

    return new Promise<boolean>((resolve) => {
      pendingCancelResolverRef.current = resolve;
    });
  };

  // Expose cancel handler to parent via ref — must be before early returns to satisfy rules of hooks
  useEffect(() => {
    if (cancelRef) {
      cancelRef.current = requestCancel;
      return () => {
        cancelRef.current = null;
      };
    }
  });

  useEffect(() => {
    return () => {
      pendingCancelResolverRef.current?.(false);
      pendingCancelResolverRef.current = null;
    };
  }, []);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center py-8">
        <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
        <span className="text-muted-foreground ml-2 text-sm">Loading email groups...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-4">
        <p className="text-destructive text-sm">{error}</p>
      </div>
    );
  }

  const handleAddInbox = () => {
    setEmailError('');
    if (!newInboxEmail.trim()) {
      setEmailError('Please enter an email address');
      return;
    }
    const success = addSharedInbox(newInboxEmail);
    if (success) {
      setNewInboxEmail('');
    } else {
      setEmailError('Invalid email or already exists');
    }
  };

  const allSelected = groups.length > 0 && selectedGroupIds.size === groups.length;

  const handleCancel = () => {
    void Promise.resolve(requestCancel()).then((canClose) => {
      if (canClose !== false) {
        onCancel?.();
      }
    });
  };

  const handleUnsavedDialogOpenChange = (open: boolean) => {
    setShowUnsavedDialog(open);
    if (!open) {
      pendingCancelResolverRef.current?.(false);
      pendingCancelResolverRef.current = null;
    }
  };

  const handleConfirmDiscard = () => {
    pendingCancelResolverRef.current?.(true);
    pendingCancelResolverRef.current = null;
    setShowUnsavedDialog(false);
  };

  return (
    <div className="flex h-full flex-col">
      <div className={cn('flex flex-1 flex-col space-y-6 overflow-y-auto py-4', className)}>
        {/* Email Groups Section */}
        <div className="flex flex-col space-y-3">
          <div>
            <h3 className="text-sm font-semibold">Email Group Access</h3>
            <p className="text-muted-foreground text-sm">Select the email groups you want to give Nova access to:</p>
          </div>

          {groups.length > 0 && (
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={allSelected ? deselectAllGroups : selectAllGroups}
                className="text-brand-400 text-sm font-bold"
                data-testid="outlook-settings-select-all"
              >
                {allSelected ? 'Deselect all' : 'Select all'}
              </button>
              <span className="text-muted-foreground text-sm whitespace-nowrap">
                {selectedGroupIds.size} group{selectedGroupIds.size !== 1 ? 's' : ''} selected
              </span>
            </div>
          )}

          {groups.length > 0 ? (
            <div className="max-h-60 space-y-2 overflow-y-auto">
              {groups.map((group) => (
                <div
                  key={group.id}
                  className="hover:bg-muted/50 bg-steel-100 flex items-start gap-3 rounded-md p-3"
                  data-testid={`outlook-settings-group-${group.id}`}
                >
                  <CheckboxInput
                    id={`group-${group.id}`}
                    checked={selectedGroupIds.has(group.id)}
                    onChange={(_e) => toggleGroup(group.id)}
                    className="mt-0.5 h-4 w-4 flex-shrink-0"
                  />
                  <label htmlFor={`group-${group.id}`} className="min-w-0 flex-1 cursor-pointer space-y-1 pl-1">
                    <div className="text-sm leading-none font-medium">{group.displayName}</div>
                    <div className="text-muted-foreground text-sm break-all">{group.mail}</div>
                  </label>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-2">
              <p className="text-muted-foreground text-sm">No email groups found.</p>
            </div>
          )}
        </div>

        {/* Shared Inboxes Section */}
        <div className="flex flex-col gap-2.5">
          <div className="space-y-1">
            <h3 className="text-sm font-semibold">Shared Inbox Access</h3>
            <p className="text-muted-foreground text-sm">Add shared inboxes you want Nova to access:</p>
          </div>

          <div className="bg-steel-100 flex items-center gap-1 rounded-md px-1.5 py-2">
            <div className="flex items-center p-[1.33px]">
              <MiniIconInformation color="#546A83" size={13.33} />
            </div>
            <p className="text-steel-400 text-[11px]">Check your permissions before adding shared inboxes</p>
          </div>

          <div className="flex flex-col space-y-3">
            <div className="flex items-start gap-2">
              <div className="flex-1">
                <Input
                  type="email"
                  placeholder="shared-inbox@example.com"
                  value={newInboxEmail}
                  onChange={(e) => {
                    setNewInboxEmail(e.target.value);
                    setEmailError('');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleAddInbox();
                    }
                  }}
                  aria-invalid={!!emailError}
                  aria-describedby={emailError ? 'shared-inbox-email-error' : undefined}
                  data-testid="outlook-settings-shared-inbox-input"
                />
                {emailError && (
                  <p id="shared-inbox-email-error" className="text-destructive mt-1 text-sm">
                    {emailError}
                  </p>
                )}
              </div>
              <Button
                onClick={handleAddInbox}
                size="default"
                className="h-10 w-10 flex-shrink-0 p-0"
                aria-label="Add shared inbox"
                data-testid="outlook-settings-add-inbox"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>

            {sharedInboxes.length > 0 && (
              <div className="space-y-2 overflow-x-hidden overflow-y-auto">
                {sharedInboxes.map((inbox) => (
                  <div
                    key={inbox.id}
                    className="hover:bg-muted/50 bg-steel-100 flex items-center gap-3 rounded-md p-1.5"
                  >
                    <div className="flex flex-1 items-center gap-3">
                      <CheckboxInput
                        id={`inbox-${inbox.id}`}
                        checked={inbox.enabled}
                        onChange={(_e) => toggleSharedInbox(inbox.id)}
                        className="h-4 w-4 flex-shrink-0"
                      />
                      <label htmlFor={`inbox-${inbox.id}`} className="flex-1 cursor-pointer">
                        <div className="text-steel-400 text-sm leading-tight break-all">{inbox.email}</div>
                      </label>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeSharedInbox(inbox.id)}
                      className="hover:bg-destructive/10 text-muted-foreground hover:text-destructive flex-shrink-0 rounded-sm p-1.5 transition-colors"
                      aria-label={`Remove shared inbox ${inbox.email}`}
                      data-testid={`outlook-settings-remove-inbox-${inbox.id}`}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {children}
      </div>

      {/* Footer — pinned to bottom */}
      <div className={cn('border-border flex shrink-0 items-center justify-end gap-2 border-t py-4', className)}>
        {onCancel && (
          <Button variant="secondary" onClick={handleCancel} data-testid="outlook-settings-cancel">
            Cancel
          </Button>
        )}
        <Button
          onClick={() => {
            save();
            onSaveSuccess?.();
          }}
          disabled={!isDirty}
          data-testid="outlook-settings-save"
        >
          Save
        </Button>
      </div>

      {/* Unsaved changes confirmation */}
      <AlertDialog open={showUnsavedDialog} onOpenChange={handleUnsavedDialogOpenChange}>
        <AlertDialogContent className="w-[349px]">
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved changes</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogDescription>
            There are unsaved changes.
            <br />
            Are you sure you want to continue?
          </AlertDialogDescription>
          <AlertDialogFooter className="flex-row justify-end">
            <AlertDialogCancel>No</AlertDialogCancel>
            <AlertDialogAction variant="dangerous" onClick={handleConfirmDiscard}>
              Yes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
